/**
 * AccessAdmin.tsx - Admin > Integrations > Access Labs.
 *
 * A copy of VibrantAdmin for Access's tables (U12). Manages the account's
 * Access ordering setup:
 *   - Test codes (labs_access_ordcode) - Access's compendium (read-only rows
 *     with no location, orderable at every location) plus the practice's own
 *     codes.
 *   - Providers (labs_access_providers) - the ordering providers (Access
 *     provider number linked to an internal user), per location.
 *   - Presets (labs_access_presets) - named test lists for a location or for
 *     all locations, built here or saved from the order form.
 *
 * Differences from Vibrant:
 *   - The compendium is ~16,000 codes, so the Test Codes tab never loads the
 *     whole list. It searches and pages on the server (KTD11) and shows nothing
 *     until something is typed. The preset builder's search is capped by the
 *     server, which reports meta.capped.
 *   - Codes Access sent with no name show under their code.
 *   - Refusals (422/404 with a code, 403 MODULE_DISABLED) map to plain
 *     messages through accessErrorMessage(); a failure with no code never shows
 *     its raw text.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Plus, FlaskConical, UserCog, BookMarked, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Tabs } from '@/components/ui/Tabs';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn, DataTableAction } from '@/components/ui/DataTable';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import { Badge } from '@/components/ui/Badge';
import { FormField } from '@/components/ui/FormField';
import { Select } from '@/components/ui/Select';
import { SearchableMultiSelect } from '@/components/ui/SearchableMultiSelect';
import {
  listAccessTests,
  createAccessTest,
  updateAccessTest,
  deleteAccessTest,
  listAccessProvidersAdmin,
  createAccessProvider,
  updateAccessProvider,
  deleteAccessProvider,
  getAccessLocations,
  listAccessPresetsAdmin,
  updateAccessPreset,
  deleteAccessPreset,
  saveAccessPreset,
  getAccessPresetContent,
  searchAccessTests,
  type AccessCatalogTest,
  type AccessProviderRow,
  type AccessLocation,
  type AccessPresetRow,
  type QuestTestCode,
  type QuestOrderNote,
} from '@/services/labs.service';
import { accessErrorMessage as sharedAccessErrorMessage, ACCESS_MODULE_DISABLED_MESSAGE } from '@/lib/accessLabsErrors';
import { ACCESS_TEST_SEARCH_MESSAGES, accessTestName } from '@/lib/accessLabs';
import * as adminSvc from '@/services/admin.service';
import { buildUserChoices } from '@/lib/userChoices';
import { parseVibrantPreset } from '@/pages/patients/labs/vibrantPreset';

/** Admin test list page size (the server allows up to 100). */
const TEST_PAGE_SIZE = 50;

/** How long typing must pause before a search is sent. */
const SEARCH_DEBOUNCE_MS = 250;

/**
 * Plain messages for the controller's refusal codes. Each says what is wrong
 * and what to do. Keep in step with AccessOrdersController.
 */
const REFUSALS: Record<string, string> = {
  MODULE_DISABLED: ACCESS_MODULE_DISABLED_MESSAGE,
  TEST_CODE_REQUIRED: 'Enter the test code, then save again.',
  TEST_NAME_REQUIRED: 'Enter the test name, then save again.',
  TEST_NOT_EDITABLE: 'Only test codes your practice added can be changed or removed. Refresh the page and try again.',
  LOCATION_CANNOT_ORDER: "That location can't order Access tests. Choose another location, or leave the location empty to offer the test at every location.",
  PROVIDER_NUMBER_REQUIRED: "Enter the provider's Access number, then save again.",
  PROVIDER_STAFF_REQUIRED: 'Choose the staff member this Access number belongs to, then save again.',
  PROVIDER_LOCATION_REQUIRED: 'Choose every location where Access has this provider number set up, then save again.',
  PROVIDER_NOT_FOUND: "That provider wasn't found. Refresh the page and try again.",
  PRESET_TITLE_REQUIRED: 'Give the preset a name, then save again.',
  PRESET_TESTS_REQUIRED: 'Add at least one test, then save the preset again.',
  PRESET_NOT_FOUND: "That preset wasn't found. It may have been removed. Refresh the page and try again.",
  DUPLICATE_TITLE: 'You already have a preset with this name. Choose a different name.',
  TEST_NOT_ORDERABLE: 'One of these tests cannot be ordered here. Remove it, or choose a different location for this preset.',
};

/**
 * Refusals whose server message names the item (the preset title, the test),
 * which says more than the generic text above. The server message is used
 * when present; the mapped text is the fallback.
 */
const NAMED_REFUSALS = new Set(['DUPLICATE_TITLE', 'TEST_NOT_ORDERABLE']);

/** The message to show for a failed Access request (see sharedAccessErrorMessage). */
function accessErrorMessage(err: unknown, fallback: string): string {
  return sharedAccessErrorMessage(err, fallback, REFUSALS, NAMED_REFUSALS);
}

/** Render a location as "Title (routingId)", or the id alone, or a dash. */
function locationLabel(location: string, routingId: string): React.ReactNode {
  if (location && routingId) return <span className="text-sm text-slate-800">{location} <span className="font-mono text-xs text-slate-500">({routingId})</span></span>;
  if (location) return <span className="text-sm text-slate-800">{location}</span>;
  if (routingId) return <span className="font-mono text-sm text-slate-500">{routingId}</span>;
  return <span className="text-slate-400">—</span>;
}

function locationOptionsFor(locations: AccessLocation[]) {
  return locations.map((l) => ({
    value: l.routingId,
    label: l.routingTitle ? `${l.routingTitle} (${l.routingId})` : l.routingId,
  }));
}

function ErrorNote({ message }: { message: string }) {
  return <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{message}</p>;
}

// ════════════════════════════════════════════════════════════════════════════
// Test Codes tab
// ════════════════════════════════════════════════════════════════════════════

interface TestForm {
  testCode: string;
  description: string;
  routingId: string;
  active: boolean;
}

const EMPTY_TEST: TestForm = {
  testCode: '',
  description: '',
  routingId: '',
  active: true,
};

function TestModal({ test, locations, onClose, onSaved }: { test?: AccessCatalogTest | null; locations: AccessLocation[]; onClose: () => void; onSaved: () => void }) {
  const isEdit = test != null;
  const [form, setForm] = useState<TestForm>(() =>
    isEdit
      ? {
          testCode: test.testCode,
          description: test.description,
          routingId: test.routingId ?? '',
          active: test.active,
        }
      : EMPTY_TEST,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function set<K extends keyof TestForm>(field: K, value: TestForm[K]) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.testCode.trim() || !form.description.trim()) return;

    const payload = {
      testCode: form.testCode.trim(),
      description: form.description.trim(),
      routingId: form.routingId.trim(),
      active: form.active,
    };

    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await updateAccessTest(test.id, payload);
      } else {
        await createAccessTest(payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(accessErrorMessage(err, "The test code couldn't be saved. Try again, and contact support if it keeps happening."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? 'Edit Test Code' : 'Add Test Code'}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving || !form.testCode.trim() || !form.description.trim()} loading={saving}>
            {isEdit ? 'Save Changes' : 'Add Test Code'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <ErrorNote message={error} />}

        <FormField label="Order Code" required htmlFor="access-test-code">
          <Input
            id="access-test-code"
            value={form.testCode}
            onChange={(e) => set('testCode', e.target.value)}
            required
            autoFocus
          />
        </FormField>

        <FormField label="Description" required htmlFor="access-test-description">
          <Input
            id="access-test-description"
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            required
          />
        </FormField>

        <FormField label="Location">
          <Select<string>
            options={locationOptionsFor(locations)}
            value={form.routingId}
            onChange={(v) => set('routingId', v ?? '')}
            placeholder="Every location"
            searchable
            clearable
          />
        </FormField>

        <FormField label="">
          <Checkbox checked={form.active} onChange={(e) => set('active', e.target.checked)} label="Active" />
        </FormField>
      </form>
    </Modal>
  );
}

function TestCodesTab({ showCreate, onCreated }: { showCreate: boolean; onCreated: () => void }) {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const [tests, setTests] = useState<AccessCatalogTest[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [locations, setLocations] = useState<AccessLocation[]>([]);
  const [editItem, setEditItem] = useState<AccessCatalogTest | null>(null);
  const [deleteItem, setDeleteItem] = useState<AccessCatalogTest | null>(null);

  useEffect(() => {
    getAccessLocations()
      .then(setLocations)
      .catch(() => setLocations([]));
  }, []);

  // A pause in typing starts a new search from the first page.
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (query === '') {
      setTests([]);
      setTotal(0);
      setHasMore(false);
      setLoadError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    listAccessTests({ search: query, page, perPage: TEST_PAGE_SIZE })
      .then((result) => {
        if (cancelled) return;
        setTests(result.tests);
        setTotal(result.total);
        setHasMore(result.hasMore);
      })
      .catch((err) => {
        if (cancelled) return;
        setTests([]);
        setLoadError(accessErrorMessage(err, "Test codes couldn't be loaded just now. Try again, and contact support if it keeps happening."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [query, page, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  async function handleDelete() {
    if (!deleteItem) return;
    setActionError(null);
    try {
      await deleteAccessTest(deleteItem.id);
      reload();
    } catch (err) {
      setActionError(accessErrorMessage(err, "The test code couldn't be deleted. Try again, and contact support if it keeps happening."));
    } finally {
      setDeleteItem(null);
    }
  }

  const columns: DataTableColumn<AccessCatalogTest>[] = [
    {
      key: 'testCode',
      header: 'Order Code',
      render: (row) => <span className="font-mono text-sm text-slate-900">{row.testCode}</span>,
    },
    {
      key: 'description',
      header: 'Description',
      render: (row) => <span className="text-slate-800">{accessTestName(row.testCode, row.description)}</span>,
    },
    {
      key: 'location',
      header: 'Location',
      render: (row) => locationLabel(row.location, row.routingId),
    },
    {
      key: 'active',
      header: 'Status',
      render: (row) => <Badge variant={row.active ? 'success' : 'neutral'}>{row.active ? 'Active' : 'Inactive'}</Badge>,
    },
  ];

  const rowActions: DataTableAction<AccessCatalogTest>[] = [
    { label: 'Edit', onClick: (row) => setEditItem(row), hidden: (row) => row.global },
    { label: 'Delete', variant: 'danger', onClick: (row) => setDeleteItem(row), hidden: (row) => row.global },
  ];

  const searching = query !== '';

  return (
    <div className="p-6">
      <div className="mb-4">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by test name or code..."
          aria-label="Search test codes"
        />
      </div>

      {actionError && <div className="mb-4"><ErrorNote message={actionError} /></div>}
      {searching && !loading && !loadError && hasMore && <p className="mb-2 text-sm text-amber-700">{ACCESS_TEST_SEARCH_MESSAGES.capped}</p>}

      {loadError ? (
        <ErrorNote message={loadError} />
      ) : (
        <DataTable<AccessCatalogTest>
          data={tests}
          columns={columns}
          keyField="id"
          loading={loading}
          rowActions={rowActions}
          emptyTitle={searching ? ACCESS_TEST_SEARCH_MESSAGES.noMatch : ACCESS_TEST_SEARCH_MESSAGES.prompt}
          emptyDescription=""
          {...(searching ? { page, pageSize: TEST_PAGE_SIZE, total, onPageChange: setPage } : {})}
        />
      )}

      {showCreate && <TestModal locations={locations} onClose={onCreated} onSaved={reload} />}
      {editItem && <TestModal test={editItem} locations={locations} onClose={() => setEditItem(null)} onSaved={reload} />}

      <ConfirmDialog
        isOpen={deleteItem !== null}
        title="Delete Test Code"
        message={deleteItem ? `Are you sure you want to delete "${deleteItem.testCode} - ${accessTestName(deleteItem.testCode, deleteItem.description)}"?` : ''}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setDeleteItem(null)}
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Providers tab
// ════════════════════════════════════════════════════════════════════════════

interface ProviderForm {
  providerId: string;
  userId: number | null;
  /** Every location Access has this number set up at (R11, as on Vibrant). */
  routingIds: string[];
}

function ProviderModal({
  provider,
  users,
  locations,
  onClose,
  onSaved,
}: {
  provider?: AccessProviderRow | null;
  users: adminSvc.AdminUser[];
  locations: AccessLocation[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = provider != null;
  const [form, setForm] = useState<ProviderForm>(() =>
    isEdit
      ? {
          providerId: provider.providerId,
          userId: provider.userId || null,
          routingIds: provider.locations.map((l) => l.routingId),
        }
      : { providerId: '', userId: null, routingIds: [] },
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // /admin/users includes deactivated staff; drop them, but keep whoever this
  // provider is already mapped to so an existing mapping stays visible.
  const userOptions = buildUserChoices(users, form.userId)
    .map((c) => ({ value: c.userId, label: c.label }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.providerId.trim() || !form.userId || form.routingIds.length === 0) return;

    const payload = { providerId: form.providerId.trim(), userId: form.userId, routingIds: form.routingIds };
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await updateAccessProvider(provider.id, payload);
      } else {
        await createAccessProvider(payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(accessErrorMessage(err, "This provider couldn't be saved. Try again, and contact support if it keeps happening."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? 'Edit Provider' : 'Add Provider'}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving || !form.providerId.trim() || !form.userId || form.routingIds.length === 0} loading={saving}>
            {isEdit ? 'Save Changes' : 'Add Provider'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <ErrorNote message={error} />}

        <FormField label="User" required>
          <Select<number>
            options={userOptions}
            value={form.userId}
            onChange={(v) => setForm((f) => ({ ...f, userId: v }))}
            placeholder="Select a user…"
            searchable
          />
        </FormField>

        <FormField label="Access Provider Number" required>
          <Input
            value={form.providerId}
            onChange={(e) => setForm((f) => ({ ...f, providerId: e.target.value }))}
            placeholder="Access provider number"
            required
          />
        </FormField>

        <FormField
          label="Locations"
          required
          helpText="Choose every location where Access has this provider number set up."
        >
          <SearchableMultiSelect
            options={locationOptionsFor(locations)}
            selectedValues={form.routingIds}
            onSelectionChange={(values) => setForm((f) => ({ ...f, routingIds: values }))}
            placeholder="Select locations…"
            searchPlaceholder="Search locations…"
            emptyMessage="No locations found."
          />
        </FormField>
      </form>
    </Modal>
  );
}

function ProvidersTab({ showCreate, onCreated }: { showCreate: boolean; onCreated: () => void }) {
  const [providers, setProviders] = useState<AccessProviderRow[]>([]);
  const [users, setUsers] = useState<adminSvc.AdminUser[]>([]);
  const [locations, setLocations] = useState<AccessLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [editItem, setEditItem] = useState<AccessProviderRow | null>(null);
  const [deleteItem, setDeleteItem] = useState<AccessProviderRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [provs, usrs, locs] = await Promise.all([listAccessProvidersAdmin(), adminSvc.listUsers(), getAccessLocations()]);
      setProviders(provs);
      setUsers(usrs);
      setLocations(locs);
    } catch (err) {
      setLoadError(accessErrorMessage(err, "Providers couldn't be loaded just now. Try again, and contact support if it keeps happening."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleDelete() {
    if (!deleteItem) return;
    setActionError(null);
    try {
      await deleteAccessProvider(deleteItem.id);
      void load();
    } catch (err) {
      setActionError(accessErrorMessage(err, "This provider couldn't be removed. Try again, and contact support if it keeps happening."));
    } finally {
      setDeleteItem(null);
    }
  }

  const columns: DataTableColumn<AccessProviderRow>[] = [
    {
      key: 'providerName',
      header: 'Provider',
      sortable: true,
      render: (row) => <span className="font-medium text-slate-900">{row.providerName || `User #${row.userId}`}</span>,
    },
    {
      key: 'providerId',
      header: 'Access Provider Number',
      render: (row) => <span className="font-mono text-sm text-slate-700">{row.providerId}</span>,
    },
    {
      key: 'locations',
      header: 'Locations',
      // An entry with no location is hidden from every order form.
      render: (row) => (
        <div className="flex flex-wrap items-center gap-1">
          {row.locations.map((l) => (
            <Badge key={l.routingId} variant="neutral">{l.routingTitle || l.routingId}</Badge>
          ))}
          {row.locations.length === 0 && <Badge variant="warning">Choose a location</Badge>}
        </div>
      ),
    },
    {
      key: 'active',
      header: 'Status',
      render: (row) => <Badge variant={row.active ? 'success' : 'neutral'}>{row.active ? 'Active' : 'Inactive'}</Badge>,
    },
  ];

  const rowActions: DataTableAction<AccessProviderRow>[] = [
    { label: 'Edit', onClick: (row) => setEditItem(row) },
    { label: 'Delete', variant: 'danger', onClick: (row) => setDeleteItem(row), hidden: (row) => !row.active },
  ];

  return (
    <div className="p-6">
      {actionError && <div className="mb-4"><ErrorNote message={actionError} /></div>}
      {loadError ? (
        <ErrorNote message={loadError} />
      ) : (
        <DataTable<AccessProviderRow>
          data={providers}
          columns={columns}
          keyField="id"
          loading={loading}
          rowActions={rowActions}
          emptyTitle="No providers yet"
          emptyDescription="Add an ordering provider's Access number and link it to a user so they can place orders."
        />
      )}

      {showCreate && <ProviderModal users={users} locations={locations} onClose={onCreated} onSaved={load} />}
      {editItem && <ProviderModal provider={editItem} users={users} locations={locations} onClose={() => setEditItem(null)} onSaved={load} />}

      <ConfirmDialog
        isOpen={deleteItem !== null}
        title="Remove Provider"
        message={deleteItem ? `Are you sure you want to remove "${deleteItem.providerName || deleteItem.providerId}"?` : ''}
        confirmLabel="Remove"
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setDeleteItem(null)}
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Presets tab
// ════════════════════════════════════════════════════════════════════════════

/** Location value meaning "every location" (stored as an empty routing id). */
const ALL_LOCATIONS = '*';

interface BuilderTest {
  testCode: string;
  description: string;
  obrNotes: QuestOrderNote[];
  /**
   * The location the code is tied to ('' = orderable everywhere). Unknown
   * (undefined) for tests loaded from a saved preset; the server re-checks
   * every code on save either way.
   */
  routingId?: string;
}

/**
 * Build or edit a preset: name it, pick the location it is for (or all
 * locations), search the codes orderable there and add them. "All locations"
 * searches only codes with no location, the only ones such a preset may hold.
 * Unlike Vibrant, nothing is listed until something is typed: the catalog is
 * too large for a useful first page.
 */
function PresetBuilderModal({
  preset,
  locations,
  onClose,
  onSaved,
}: {
  preset?: AccessPresetRow | null;
  locations: AccessLocation[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = preset != null;
  const presetId = preset?.id;
  const [title, setTitle] = useState(preset?.title ?? '');
  const [shared, setShared] = useState(preset?.shared ?? true);
  const [scope, setScope] = useState<string>(preset?.routingId ? preset.routingId : ALL_LOCATIONS);
  const [tests, setTests] = useState<BuilderTest[]>([]);
  const [loadingTests, setLoadingTests] = useState(isEdit);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<QuestTestCode[]>([]);
  const [capped, setCapped] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const locationOptions = [
    { value: ALL_LOCATIONS, label: 'All locations' },
    ...locationOptionsFor(locations),
  ];

  // Editing: load the tests the preset holds today.
  useEffect(() => {
    if (presetId == null) return;
    let cancelled = false;
    getAccessPresetContent('preset', presetId)
      .then((content) => {
        if (cancelled) return;
        const parsed = parseVibrantPreset(content.fullOrderContent, 'preset', '');
        setTests(parsed.tests.map((t) => ({ testCode: t.testCode, description: t.description, obrNotes: t.obrNotes, routingId: t.routingId })));
        // Tests that can no longer be ordered are left out by the server;
        // saving writes the preset without them.
        const gone = content.unavailableTests ?? [];
        if (gone.length > 0) {
          const names = gone.map((g) => (g.description ? `${g.description} (${g.testCode})` : g.testCode)).join(', ');
          setNotice(
            `Removed ${gone.length === 1 ? '1 test' : `${gone.length} tests`} that can no longer be ordered: ${names}. Save to update the preset.`,
          );
        }
      })
      .catch(() => {
        if (!cancelled) setError("This preset's tests couldn't be loaded. Close this window and try again.");
      })
      .finally(() => {
        if (!cancelled) setLoadingTests(false);
      });
    return () => {
      cancelled = true;
    };
  }, [presetId]);

  // Search the codes orderable for the chosen location (debounced), once
  // something has been typed.
  useEffect(() => {
    const term = search.trim();
    if (term === '') {
      setResults([]);
      setCapped(false);
      setSearched(false);
      setSearching(false);
      setSearchError(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      setSearching(true);
      setSearchError(null);
      const request =
        scope === ALL_LOCATIONS
          ? searchAccessTests(term, undefined, { globalOnly: true })
          : searchAccessTests(term, scope);
      request
        .then((found) => {
          if (cancelled) return;
          setResults(found.tests);
          setCapped(found.capped);
          setSearched(true);
        })
        .catch((err) => {
          if (cancelled) return;
          setResults([]);
          setCapped(false);
          setSearchError(accessErrorMessage(err, "Tests couldn't be searched just now. Try again, and contact support if it keeps happening."));
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search, scope]);

  function changeScope(next: string) {
    const nextRoutingId = next === ALL_LOCATIONS ? '' : next;
    // A location's own codes cannot come along to a different location.
    const kept = tests.filter((t) => t.routingId === undefined || t.routingId === '' || t.routingId === nextRoutingId);
    const dropped = tests.length - kept.length;
    setScope(next);
    setTests(kept);
    setNotice(
      dropped > 0
        ? `Removed ${dropped} ${dropped === 1 ? 'test' : 'tests'} that cannot be ordered at the new location.`
        : null,
    );
  }

  function addTest(row: QuestTestCode) {
    setTests((prev) =>
      prev.some((t) => t.testCode === row.testCode)
        ? prev
        : [...prev, { testCode: row.testCode, description: row.description, obrNotes: [], routingId: row.routingId ?? '' }],
    );
  }

  function removeTest(testCode: string) {
    setTests((prev) => prev.filter((t) => t.testCode !== testCode));
  }

  const canSave = title.trim() !== '' && tests.length > 0 && !saving && !loadingTests;

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    if (!canSave) return;

    const routingId = scope === ALL_LOCATIONS ? '' : scope;
    const payloadTests = tests.map(({ testCode, description, obrNotes }) => ({ testCode, description, obrNotes }));

    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await updateAccessPreset(preset.id, { title: title.trim(), shared: shared ? '1' : '0', routingId, tests: payloadTests });
      } else {
        await saveAccessPreset({
          presetTitle: title.trim(),
          presetShared: shared ? '1' : '0',
          routingId,
          tests: payloadTests,
          createOnly: true,
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(accessErrorMessage(err, "The preset couldn't be saved. Try again, and contact support if it keeps happening."));
    } finally {
      setSaving(false);
    }
  }

  const selectedCodes = new Set(tests.map((t) => t.testCode));

  let resultsMessage: string | null = null;
  if (search.trim() === '') resultsMessage = ACCESS_TEST_SEARCH_MESSAGES.prompt;
  else if (searchError) resultsMessage = searchError;
  else if (results.length === 0) resultsMessage = searching || !searched ? 'Searching...' : ACCESS_TEST_SEARCH_MESSAGES.noMatch;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? 'Edit Preset' : 'Add Preset'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSave} loading={saving}>
            {isEdit ? 'Save Changes' : 'Add Preset'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <ErrorNote message={error} />}

        <FormField label="Title" required>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Annual Wellness" required autoFocus />
        </FormField>

        <FormField label="Location">
          <Select<string>
            options={locationOptions}
            value={scope}
            onChange={(v) => changeScope(v ?? ALL_LOCATIONS)}
            searchable
          />
        </FormField>

        <FormField label="">
          <Checkbox checked={shared} onChange={(e) => setShared(e.target.checked)} label="Shared with all users on this account" />
        </FormField>

        <FormField label="Add Tests">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by test name or code..."
            aria-label="Search tests"
          />
          {capped && results.length > 0 && <p className="mt-2 text-sm text-amber-700">{ACCESS_TEST_SEARCH_MESSAGES.capped}</p>}
          <div className="mt-2 max-h-56 overflow-y-auto rounded border border-slate-200" data-testid="preset-test-results">
            {resultsMessage !== null ? (
              <p className={searchError ? 'px-3 py-2 text-sm text-red-600' : 'px-3 py-2 text-sm text-slate-500'}>{resultsMessage}</p>
            ) : (
              results.map((row) => {
                const added = selectedCodes.has(row.testCode);
                return (
                  <button
                    key={`${row.testCode}|${row.routingId ?? ''}`}
                    type="button"
                    onClick={() => addTest(row)}
                    disabled={added}
                    className="flex w-full items-center gap-2 border-b border-slate-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-slate-50 disabled:cursor-default disabled:bg-slate-50 disabled:text-slate-400"
                  >
                    <span className="font-mono text-xs text-slate-500">{row.testCode}</span>
                    <span className="flex-1 text-slate-800">{accessTestName(row.testCode, row.description)}</span>
                    {added ? <span className="text-xs">Added</span> : <Plus className="h-4 w-4 text-slate-400" />}
                  </button>
                );
              })
            )}
          </div>
        </FormField>

        <FormField label={`Tests in this preset (${tests.length})`}>
          {notice && <p className="mb-2 text-sm text-amber-700">{notice}</p>}
          {loadingTests ? (
            <p className="text-sm text-slate-500">Loading...</p>
          ) : tests.length === 0 ? (
            <p className="text-sm text-slate-500">No tests added yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100 rounded border border-slate-200" data-testid="preset-selected-tests">
              {tests.map((t) => {
                const name = accessTestName(t.testCode, t.description);
                return (
                  <li key={t.testCode} className="flex items-center gap-2 px-3 py-2 text-sm">
                    <span className="font-mono text-xs text-slate-500">{t.testCode}</span>
                    <span className="flex-1 text-slate-800">{name}</span>
                    <button
                      type="button"
                      onClick={() => removeTest(t.testCode)}
                      className="text-slate-400 hover:text-red-600"
                      aria-label={`Remove ${t.description || t.testCode}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </FormField>
      </form>
    </Modal>
  );
}

function PresetsTab({ showCreate, onCreated }: { showCreate: boolean; onCreated: () => void }) {
  const [presets, setPresets] = useState<AccessPresetRow[]>([]);
  const [locations, setLocations] = useState<AccessLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [editItem, setEditItem] = useState<AccessPresetRow | null>(null);
  const [deleteItem, setDeleteItem] = useState<AccessPresetRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [rows, locs] = await Promise.all([listAccessPresetsAdmin(), getAccessLocations()]);
      setPresets(rows);
      setLocations(locs);
    } catch (err) {
      setLoadError(accessErrorMessage(err, "Presets couldn't be loaded just now. Try again, and contact support if it keeps happening."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleDelete() {
    if (!deleteItem) return;
    setActionError(null);
    try {
      await deleteAccessPreset(deleteItem.id);
      void load();
    } catch (err) {
      setActionError(accessErrorMessage(err, "The preset couldn't be deleted. Try again, and contact support if it keeps happening."));
    } finally {
      setDeleteItem(null);
    }
  }

  const columns: DataTableColumn<AccessPresetRow>[] = [
    {
      key: 'title',
      header: 'Title',
      sortable: true,
      render: (row) => <span className="font-medium text-slate-900">{row.title}</span>,
    },
    {
      key: 'location',
      header: 'Location',
      sortable: true,
      render: (row) => locationLabel(row.location, row.routingId),
    },
    {
      key: 'testCount',
      header: 'Tests',
      render: (row) => <span className="text-sm text-slate-600">{row.testCount}</span>,
    },
    {
      key: 'shared',
      header: 'Shared',
      render: (row) => <Badge variant={row.shared ? 'info' : 'neutral'}>{row.shared ? 'Shared' : 'Private'}</Badge>,
    },
  ];

  const rowActions: DataTableAction<AccessPresetRow>[] = [
    { label: 'Edit', onClick: (row) => setEditItem(row) },
    { label: 'Delete', variant: 'danger', onClick: (row) => setDeleteItem(row) },
  ];

  return (
    <div className="p-6">
      {actionError && <div className="mb-4"><ErrorNote message={actionError} /></div>}
      {loadError ? (
        <ErrorNote message={loadError} />
      ) : (
        <DataTable<AccessPresetRow>
          data={presets}
          columns={columns}
          keyField="id"
          loading={loading}
          rowActions={rowActions}
          emptyTitle="No presets yet"
          emptyDescription=""
        />
      )}

      {showCreate && <PresetBuilderModal locations={locations} onClose={onCreated} onSaved={load} />}
      {editItem && <PresetBuilderModal preset={editItem} locations={locations} onClose={() => setEditItem(null)} onSaved={load} />}

      <ConfirmDialog
        isOpen={deleteItem !== null}
        title="Delete Preset"
        message={deleteItem ? `Are you sure you want to delete the preset "${deleteItem.title}"?` : ''}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setDeleteItem(null)}
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Page
// ════════════════════════════════════════════════════════════════════════════

export function AccessAdmin() {
  const [activeTab, setActiveTab] = useState('tests');
  const [showCreate, setShowCreate] = useState(false);

  const addLabel = activeTab === 'providers' ? 'Add Provider' : activeTab === 'presets' ? 'Add Preset' : 'Add Test Code';

  return (
    <div>
      <div className="px-6 pt-6">
        <PageHeader
          title="Access Labs"
          actions={
            <Button onClick={() => setShowCreate(true)}>
              <Plus className="h-4 w-4 mr-1" />
              {addLabel}
            </Button>
          }
        />
      </div>

      <Tabs
        tabs={[
          { id: 'tests', label: 'Test Codes', icon: <FlaskConical className="h-4 w-4" />, content: <TestCodesTab showCreate={showCreate && activeTab === 'tests'} onCreated={() => setShowCreate(false)} /> },
          { id: 'providers', label: 'Providers', icon: <UserCog className="h-4 w-4" />, content: <ProvidersTab showCreate={showCreate && activeTab === 'providers'} onCreated={() => setShowCreate(false)} /> },
          { id: 'presets', label: 'Presets', icon: <BookMarked className="h-4 w-4" />, content: <PresetsTab showCreate={showCreate && activeTab === 'presets'} onCreated={() => setShowCreate(false)} /> },
        ]}
        onChange={(id) => { setActiveTab(id); setShowCreate(false); }}
      />
    </div>
  );
}
